// The survey's own destructive action (redesign spec §7). The server refuses
// a delete while the survey has responses (they are never auto-deleted), so
// that is said up front instead of offering a button that would fail. Its
// placements go with it. The rule and the confirmation are shared with the
// list's row menu (delete-survey-dialog.tsx).

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { surveyKeys } from "@/api/keys";
import type { Survey } from "@/api/types";
import { DangerZoneSection } from "@/components/section-page/danger-zone";
import { DeleteSurveyDialog, surveyDeleteRefusal } from "./delete-survey-dialog";

export function SurveyDangerZone({ survey, workspaceId }: { survey: Survey; workspaceId: string }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const queryClient = useQueryClient();

    return (
        <>
            <DangerZoneSection
                description="Deletes the survey with its steps and placements. This can't be undone."
                action="Delete survey"
                disabledReason={surveyDeleteRefusal(survey.responseCount)}
                onAction={() => setOpen(true)}
            />
            <DeleteSurveyDialog
                workspaceId={workspaceId}
                survey={survey}
                open={open}
                onOpenChange={setOpen}
                onDeleted={() => navigate({ to: "/w/$workspaceId/surveys", params: { workspaceId } })}
                // A refusal means a response landed since load. The mutation has
                // refreshed the survey by now, so say it as the page does.
                refusalAfterConflict={() => {
                    const fresh = queryClient.getQueryData<Survey>(surveyKeys.detail(survey.id));
                    return surveyDeleteRefusal(fresh?.responseCount ?? 0);
                }}
            />
        </>
    );
}
